import { Menu } from "@hellocarbo/lasagne";
import {
  ArrowLeftRight,
  BookOpen,
  Building2,
  Calculator,
  ChartColumnDecreasing,
  CircleHelp,
  Eye,
  Headset,
  House,
  Library,
  LogOut,
  PieChart,
  PlusCircle,
  Ruler,
  Send,
  Settings,
  Star,
  User,
  Users,
} from "lucide-react";
import { use, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useMatches } from "react-router";

import logo from "@/assets/logo.svg";
import {
  FeaturesContext,
  PortalContainerContext,
  UserContext,
} from "@/contexts";
import { CustomUIMatch } from "@/hooks/useBreadcrumbs";
import useSegmentEvent from "@/hooks/useSegmentEvent";
import { TTrackingCampaign } from "@/types";
import { formatEndDate } from "@/utils/formatDate";

export enum NavigationMenuLinks {
  index = "index",
  collect = "collect",
  restitution = "restitution",
  communicate = "communicate",
  trajectory = "trajectory",
  simulate = "simulate",
  users = "users",
  myAccount = "myAccount",
  myCompany = "myCompany",
  switchEntity = "switchEntity",
  options = "options",
  lca = "lca",
}

interface NavigationMenuProps {
  trackingCampaigns: TTrackingCampaign[];
  handleLogOutClick: () => void;
  hasSimulations?: boolean;
}

const NavigationMenu = ({
  trackingCampaigns,
  handleLogOutClick,
  hasSimulations,
}: NavigationMenuProps) => {
  const { container } = use(PortalContainerContext);
  const { user } = use(UserContext);
  const { featureFlags } = use(FeaturesContext);
  const { sendSegmentEvent } = useSegmentEvent();
  const { t } = useTranslation(["navigation-menu", "formatter"]);
  const [currentTrackingCampaign, setCurrentTrackingCampaign] = useState<
    TTrackingCampaign | undefined
  >();
  const [activeMenuLink, setActiveMenuLink] = useState<
    NavigationMenuLinks | undefined
  >();
  const matches = useMatches() as CustomUIMatch[];
  const location = useLocation();

  const filteredMatches = matches
    // first get rid of any matches that don't have handle and breadcrumb
    .filter((match) => Boolean(match.handle));

  const lastMatch = filteredMatches[filteredMatches.length - 1];

  const hasAtLeastOneFinishedInternalTrackingCampaign = trackingCampaigns.some(
    (tc) => tc.finished && !tc.score.is_external
  );
  const isAdmin = user?.role === "admin";
  const isPremiumUser = user?.company?.subscriptionType === "premium";
  const isHybridEntity =
    user?.company?.subscriptionType === "hybrid" && user?.company?.hasParent;
  const isFreeTrial = user?.company?.subscriptionType === "start";
  const isPaused = user?.company?.subscriptionType === "pause";
  const isTrialFinished = user?.company?.isTrialFinished;
  const currentPath = location.pathname;

  const getDisabledTooltipContent = ({
    needsFinishedTrackingCampaign,
    premiumOnly,
    availableForFreeTrial,
    availableForHybridEntity = true,
  }: {
    needsFinishedTrackingCampaign: boolean;
    premiumOnly: boolean;
    availableForFreeTrial: boolean;
    availableForHybridEntity?: boolean;
  }) => {
    // Message for features unavailable to entities (companies with a parent) with a hybrid subscription.
    if (!availableForHybridEntity && isHybridEntity) {
      return t("tooltips.notAvailableForHybrid");
    }

    // Specific message for features unavailable during free trial.
    if (!availableForFreeTrial) {
      return t("tooltips.freeTrial.switchToPaid");
    }

    // Message for premium-only features when user is not premium (and not on trial for this check).
    if (premiumOnly && !isFreeTrial && !isPremiumUser) {
      return t("tooltips.upgradeToPremium");
    }

    // Messages related to needing a finished tracking campaign.
    if (needsFinishedTrackingCampaign) {
      // Specific message during free trial if campaign isn't finished.
      if (isFreeTrial) {
        return t("tooltips.freeTrial.finishYourCollect");
      }
      // Message if user has finished campaigns but the current one isn't finished.
      if (hasAtLeastOneFinishedInternalTrackingCampaign) {
        return t("tooltips.selectAValidatedCarbonFootprint");
      }
      // Default message if no campaigns are finished yet.
      return t("tooltips.validateYourCarbonFootprint");
    }
  };

  const onTrackingCampaignChange = (id: string) => {
    const selectedId = Number(id);
    const foundTrackingCampaign = trackingCampaigns.find(
      (tc) => tc.id === selectedId
    );

    // Update the state with the newly selected campaign object.
    setCurrentTrackingCampaign(foundTrackingCampaign);
    // If the user is on communicate
    if (
      currentPath.includes("/reviews/communicate") &&
      currentPath !== `/reviews/communicate/${foundTrackingCampaign?.review_id}`
    ) {
      // If the new campaign is finished, go to its communicate page.
      if (foundTrackingCampaign?.finished) {
        window.location.href = `/reviews/communicate/${foundTrackingCampaign?.review_id}`;
      } else {
        // Otherwise, redirect to the collect page.
        window.location.href = `/scores/${foundTrackingCampaign?.score.id}/categories`;
      }
    }
    // If the user is on a reviews
    else if (
      currentPath.includes("/reviews") &&
      currentPath !== `/reviews/${foundTrackingCampaign?.review_id}`
    ) {
      // If the new campaign is finished, go to its review page.
      if (foundTrackingCampaign?.finished) {
        window.location.href = `/reviews/${foundTrackingCampaign?.review_id}`;
      } else {
        // Otherwise, redirect to the home/dashboard page.
        window.location.href = `/scores/${foundTrackingCampaign?.score.id}/categories`;
      }
    }
    // If the user is on any collect page
    else if (
      ((currentPath.includes("/scores") || currentPath.includes("/tracking")) &&
        currentPath !==
          `/scores/${foundTrackingCampaign?.score.id}/categories`) ||
      currentPath.includes("/survey")
    ) {
      // Go to the main collect page
      window.location.href = `/scores/${foundTrackingCampaign?.score.id}/categories`;
    }
  };

  const onNewCarbonFootprint = () => {
    window.location.href = `/tracking/campaigns/new`;
  };

  const onNewExternalCarbonFootprint = () => {
    window.location.href = `/tracking/external_campaigns/new`;
  };

  /**
   * Effect to persist the ID of the currently selected tracking campaign
   * to local storage whenever it changes. So it's remembered when the user
   * navigates to another page or refreshes the page.
   */
  useEffect(() => {
    if (currentTrackingCampaign) {
      localStorage.setItem(
        "currentTrackingCampaign",
        `${currentTrackingCampaign?.id}`
      );
      localStorage.setItem(
        "currentScoreId",
        `${currentTrackingCampaign?.score.id}`
      );
      localStorage.setItem(
        "currentReviewId",
        `${currentTrackingCampaign?.review_id}`
      );
    }
  }, [currentTrackingCampaign]);

  /**
   * Effect to determine and set the initial or current tracking campaign based on the URL path
   * or fall back to local storage or the latest campaign.
   * This runs when the available campaigns change or the URL path changes.
   */
  useEffect(() => {
    let foundTrackingCampaign: TTrackingCampaign | undefined;

    // Prioritize finding the campaign based on IDs present in the URL.
    if (currentPath.includes("/reviews/communicate")) {
      // Extract review_id from /reviews/communicate/:review_id
      const reviewId = Number(currentPath.split("/")[3]);
      foundTrackingCampaign = trackingCampaigns.find(
        (tc) => tc.review_id === reviewId
      );
    } else if (currentPath.includes("/reviews")) {
      // Extract review_id from /reviews/:review_id
      const reviewId = Number(currentPath.split("/")[2]);
      foundTrackingCampaign = trackingCampaigns.find(
        (tc) => tc.review_id === reviewId
      );
    } else if (currentPath.includes("/scores")) {
      // Extract score.id from /scores/:score_id/...
      const scoreId = Number(currentPath.split("/")[2]);
      foundTrackingCampaign = trackingCampaigns.find(
        (tc) => tc.score.id === scoreId
      );
    }

    // If no campaign was found based on the URL...
    if (!foundTrackingCampaign) {
      // Try to retrieve the last selected campaign ID from local storage.
      const storedId = localStorage.getItem("currentTrackingCampaign");
      if (storedId) {
        foundTrackingCampaign = trackingCampaigns.find(
          (tc) => tc.id === Number(storedId)
        );
      }
      // If still not found (e.g., invalid stored ID or first visit),
      // default to the latest campaign in the provided list since they are sorted by date.
      if (!foundTrackingCampaign && trackingCampaigns.length > 0) {
        foundTrackingCampaign = trackingCampaigns[trackingCampaigns.length - 1];
      }
    }

    // Set the determined tracking campaign in the state.
    setCurrentTrackingCampaign(foundTrackingCampaign);

    // Set the active menu link in the state.
    setActiveMenuLink(
      lastMatch?.handle.activeMenuLink?.(
        lastMatch.params,
        foundTrackingCampaign?.score.id.toString()
      )
    );
  }, [trackingCampaigns, currentPath, lastMatch]);

  const companyItems = [
    // Switch Entity (available to all users if their company has subsidiaries)
    ...(user?.subsidiaries.length
      ? [
          {
            Icon: ArrowLeftRight,
            href: "/subsidiary-choice",
            title: t("pages.switchEntity"),
            active: activeMenuLink === NavigationMenuLinks.switchEntity,
          },
        ]
      : []),
    ...(isAdmin
      ? [
          // My Company (Admin only)
          {
            Icon: Building2,
            href: `/companies/${user?.company.public_id}/edit`,
            title: t("pages.myCompany"),
            active: activeMenuLink === NavigationMenuLinks.myCompany,
          },
          // Users management (Admin only)
          {
            Icon: Users,
            href: `/users`,
            title: t("pages.users"),
            active: activeMenuLink === NavigationMenuLinks.users,
          },
          // Options (Admin only and subscribed company)
          ...(!isFreeTrial
            ? [
                {
                  Icon: PlusCircle,
                  href: `/options`,
                  title: t("pages.options"),
                  active: activeMenuLink === NavigationMenuLinks.options,
                },
              ]
            : []),
        ]
      : []),
  ];
  const hasCompanyItems = companyItems.length > 0;

  const subItemGroups = [
    // Company group containing entity and account management items
    ...(hasCompanyItems
      ? [
          {
            title: t("subItemGroups.company"),
            items: companyItems,
          },
        ]
      : []),

    // Preferences group visible to all users
    {
      title: hasCompanyItems ? t("subItemGroups.account") : undefined,
      items: [
        // My Account (available to all users)
        {
          Icon: Settings,
          href: "/users/edit",
          title: t("pages.myAccount"),
          active: activeMenuLink === NavigationMenuLinks.myAccount,
        },
        // Sign Out (available to all users)
        {
          Icon: LogOut,
          onClick: handleLogOutClick,
          title: t("pages.signOut"),
        },
      ],
    },
  ];

  return (
    <Menu
      // Company logo displayed at the top of the menu
      logoUrl={logo}
      defaultUrl="/"
      closeOnSubItemClick
      homeItem={
        isAdmin && !isFreeTrial
          ? {
              Icon: House,
              href: `/`,
              title: t("pages.home"),
              active: activeMenuLink === NavigationMenuLinks.index,
            }
          : undefined // Don't show home item for non-admins
      }
      portalContainer={container as Element}
      // Configuration for the context switcher (tracking campaign selector)
      contextSwitcher={{
        // List of available tracking campaigns
        contexts: trackingCampaigns.map((tc) => ({
          id: `${tc.id}`,
          // Display name for the campaign, formatted like "Start date - End date"
          name: `${t("formatter:date", {
            value: new Date(tc.period_start),
          })} - ${t("formatter:date", {
            value: formatEndDate(new Date(tc.period_end)),
          })}`,
          // Text information indicating if the campaign is external or internal
          text: tc.score.is_external
            ? t("trackingCampaignSwitcher.score.external")
            : t("trackingCampaignSwitcher.score.internal"),
          // Status indicator for the campaign
          status: {
            name:
              tc.finished || (isFreeTrial && isTrialFinished)
                ? t("trackingCampaignSwitcher.status.finished")
                : isPaused
                  ? t("trackingCampaignSwitcher.status.paused")
                  : t("trackingCampaignSwitcher.status.inProgress"),
            state:
              tc.finished || (isFreeTrial && isTrialFinished)
                ? "positive"
                : "warning",
          },
        })),
        label: t("trackingCampaignSwitcher.label"),
        selectedLabel: t("trackingCampaignSwitcher.selectedLabel"),
        newContextButton:
          !isFreeTrial && isAdmin
            ? {
                label: t("trackingCampaignSwitcher.newContextButton"),
                onClick: onNewCarbonFootprint,
              }
            : undefined,
        secondaryButton:
          !isFreeTrial && isAdmin
            ? {
                label: t("trackingCampaignSwitcher.secondaryButton"),
                onClick: onNewExternalCarbonFootprint,
              }
            : undefined,
        onChange: onTrackingCampaignChange,
        selectedContextId: `${currentTrackingCampaign?.id}`,
      }}
      // Main navigation groups/sections
      groups={[
        // --- Group 1 ---
        {
          items: [
            // Collect Page
            {
              Icon: Calculator,
              href: `/scores/${currentTrackingCampaign?.score.id}/categories`,
              title: t("pages.measure"),
              active: activeMenuLink === NavigationMenuLinks.collect,
            },
            // Review Page (Admin only)
            ...(isAdmin
              ? [
                  {
                    Icon: PieChart,
                    // Link differs for free trial vs paid
                    href: isFreeTrial
                      ? "/trial_result"
                      : `/reviews/${currentTrackingCampaign?.review_id}`,
                    title: t("pages.analyse"),
                    // Disabled logic: Hybrid entities have no access, trial users need trial finished, others need at least one finished campaign.
                    disabled:
                      isHybridEntity ||
                      (isFreeTrial
                        ? !isTrialFinished
                        : !currentTrackingCampaign?.finished),
                    disabledTooltipContent: getDisabledTooltipContent({
                      needsFinishedTrackingCampaign: true,
                      premiumOnly: false,
                      availableForFreeTrial: true,
                      availableForHybridEntity: false,
                    }),
                    active: activeMenuLink === NavigationMenuLinks.restitution,
                  },
                ]
              : []),
            // Communicate Page (Admin only)
            ...(isAdmin
              ? [
                  {
                    Icon: Send,
                    // Link differs for free trial vs paid/finished trial
                    href: isFreeTrial
                      ? "/reviews/communicate/free-trial"
                      : `/reviews/communicate/${currentTrackingCampaign?.review_id}`,
                    title: t("pages.share"),
                    // Disabled logic: Similar to Review page
                    disabled:
                      isHybridEntity ||
                      (isFreeTrial
                        ? !isTrialFinished
                        : !currentTrackingCampaign?.finished),
                    disabledTooltipContent: getDisabledTooltipContent({
                      needsFinishedTrackingCampaign: true,
                      premiumOnly: false,
                      availableForFreeTrial: true,
                      availableForHybridEntity: false,
                    }),
                    active: activeMenuLink === NavigationMenuLinks.communicate,
                  },
                ]
              : []),
          ],
        },
        // --- Groups 2 and 3 (Admin only) ---
        ...(isAdmin
          ? [
              {
                title: t("navigation-menu:sections.goFurther"),
                items: [
                  // Reduce Page
                  {
                    Icon: ChartColumnDecreasing,
                    href: `/trajectory/creation/introduction`,
                    title: t("pages.reduce"),
                    active: activeMenuLink === NavigationMenuLinks.trajectory,
                    // Disabled logic: Trial needs completion; other plans need at least one finished campaign.
                    disabled: isFreeTrial
                      ? !isTrialFinished
                      : !hasAtLeastOneFinishedInternalTrackingCampaign,
                    disabledTooltipContent: getDisabledTooltipContent({
                      needsFinishedTrackingCampaign: true,
                      premiumOnly: true,
                      availableForFreeTrial: true,
                    }),
                  },
                  ...(!isFreeTrial
                    ? [
                        {
                          Icon: Ruler,
                          href: "/lca",
                          title: t("pages.carboLCA"),
                          onClick: () => sendSegmentEvent("clic_menu_acv_A"),
                          active: activeMenuLink === NavigationMenuLinks.lca,
                        },
                        {
                          Icon: Eye,
                          href: hasSimulations
                            ? "/simulations"
                            : "/simulations/scenarios",
                          title: t("pages.carbonCalculator"),
                          active:
                            activeMenuLink === NavigationMenuLinks.simulate,
                          onClick: () =>
                            sendSegmentEvent("clic_menu_calculatrice"),
                        },
                      ]
                    : []),
                ],
              },
            ]
          : []),
      ]}
      // Optional highlight section at the bottom, shown during free trial
      highlight={
        isFreeTrial
          ? {
              title: t("freeTrialHighlight.title"),
              Icon: Star,
              buttonText: t("freeTrialHighlight.buttonText"),
              href: "/pricing",
            }
          : undefined
      }
      // Footer items
      footer={[
        // Resources Menu
        {
          Icon: BookOpen,
          portalContainer: container as Element,
          subItemGroups: [
            {
              items: [
                {
                  Icon: Library,
                  href: "https://academy.hellocarbo.com/",
                  shouldOpenNewTab: true,
                  title: t("pages.carboAcademy"),
                },
                {
                  Icon: CircleHelp,
                  href: "https://hellocarbo-25361575.hs-sites-eu1.com/knowledge",
                  shouldOpenNewTab: true,
                  title: t("pages.helpCenter"),
                },
                {
                  Icon: Headset,
                  href: "/coaching",
                  title: t("pages.videoAssistance"),
                },
              ],
            },
          ],
          title: t("pages.resources"),
          // Disabled logic: Not available during free trial.
          disabled: isFreeTrial,
          disabledTooltipContent: getDisabledTooltipContent({
            needsFinishedTrackingCampaign: false,
            premiumOnly: false,
            availableForFreeTrial: false,
          }),
        },
        // User/Account Menu
        {
          Icon: User,
          portalContainer: container as Element,
          subItemGroups,
          subtitle: user?.company.name,
          title: t(`roles.${user?.role}`),
        },
      ]}
    />
  );
};

export default NavigationMenu;
